/**
 * SitCard -- displays a single hunt sit with its metadata and photo thumbnails.
 *
 * M81 migration: replaced the legacy URL.createObjectURL useEffect with
 * PhotoThumbnail components that use usePhotoSrc for on-demand hydration.
 * M83 (ADR-044): bytes are stored as ArrayBuffer in IDB; usePhotoSrc
 * reconstructs the Blob at render time.
 */

import { useState, useEffect } from 'react';
import { format, parseISO } from 'date-fns';
import { Link } from 'react-router-dom';
import { X, Pencil, Sunrise, Sunset, Sun, Thermometer, Wind } from 'lucide-react';

import type { HuntSit } from '@/types/hunt';
import type { PhotoRecord } from '@/types/photo';
import { getPhotosBySitId } from '@/services/photo-service';
import { PhotoLightbox } from '@/components/photos/PhotoLightbox';
import { PhotoThumbnail } from '@/components/photos/PhotoThumbnail';

interface SitCardProps {
  sit: HuntSit;
  locationName: string;
  locationCategory?: string;
  huntId: string;
  onDelete: (sitId: string) => void;
  onEdit?: (sitId: string) => void;
}

/**
 * Load photos for a sit. Re-fetches whenever the sit's updatedAt changes,
 * which photo-service bumps after attaching new photos via addPhotosToSit.
 */
function useSitPhotos(sitId: string, updatedAt: string) {
  const [photos, setPhotos] = useState<PhotoRecord[]>([]);

  useEffect(() => {
    getPhotosBySitId(sitId)
      .then(setPhotos)
      .catch((err) => console.error('[SitCard] Failed to load sit photos:', err));
  }, [sitId, updatedAt]);

  return photos;
}

export function SitCard({ sit, locationName, locationCategory, huntId, onDelete, onEdit }: SitCardProps) {
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  const photos = useSitPhotos(sit.id, sit.updatedAt);

  const periodLabel =
    sit.period === 'morning' ? 'Morning' : sit.period === 'evening' ? 'Evening' : 'All Day';

  const dataPeriod =
    sit.period === 'morning' ? 'morning' : sit.period === 'evening' ? 'evening' : 'all-day';

  const timeRange =
    sit.timeIn && sit.timeOut
      ? `${sit.timeIn} -> ${sit.timeOut}`
      : sit.timeIn
        ? `In: ${sit.timeIn}`
        : null;

  const observationCount = sit.observations?.length ?? 0;

  const hasClimate = Boolean(
    sit.conditions && (sit.conditions.tempF !== undefined || sit.conditions.windSpeedMph !== undefined),
  );

  // Show up to 3 photo thumbnails
  const thumbnailPhotos = photos.slice(0, 3);

  return (
    <div
      className="bg-bark-800 border-[0.5px] border-bark-600 rounded-lg shadow-card p-4"
      data-testid={`sit-card-${sit.id}`}
      data-has-climate={hasClimate ? 'true' : 'false'}
      data-period={dataPeriod}
    >
      <div className="flex items-start gap-3">
        <div className="flex flex-col gap-1 min-w-0 flex-1">
          {/* Location category as field-label above location name */}
          {locationCategory && (
            <p className="field-label">
              {locationCategory}
            </p>
          )}
          <p className="text-body-md text-[var(--color-text-primary)] font-medium">
            {locationName}
          </p>

          {/* Period with icon */}
          <div className="flex items-center gap-1 text-body-sm text-[var(--color-text-muted)]">
            {sit.period === 'morning' ? (
              <Sunrise size={14} aria-hidden className="text-[var(--color-text-muted)]" />
            ) : sit.period === 'evening' ? (
              <Sunset size={14} aria-hidden className="text-[var(--color-text-muted)]" />
            ) : (
              <Sun size={14} aria-hidden className="text-[var(--color-text-muted)]" />
            )}
            <span>{periodLabel}</span>
            <span>·</span>
            <span>{format(parseISO(sit.date), 'MMM d, yyyy')}</span>
          </div>

          {timeRange && (
            <p className="text-body-xs text-[var(--color-text-muted)]">{timeRange}</p>
          )}
          {sit.notes && (
            <p className="text-body-xs text-[var(--color-text-muted)] truncate">{sit.notes}</p>
          )}
          {observationCount > 0 && (
            <p className="text-body-xs text-[var(--color-accent-hover)]">
              {observationCount} observation{observationCount !== 1 ? 's' : ''}
            </p>
          )}

          {/* Sit photo thumbnails -- each uses usePhotoSrc via PhotoThumbnail (M81) */}
          {thumbnailPhotos.length > 0 && (
            <div
              className="flex gap-1 mt-1"
              data-testid={`sit-photo-strip-${sit.id}`}
            >
              {thumbnailPhotos.map((photo, idx) => (
                <div
                  key={photo.id}
                  className="w-10 h-10 rounded-sm overflow-hidden border border-[var(--color-border)] hover:border-[var(--color-border-subtle)] transition-colors"
                  data-testid={`sit-photo-thumb-${photo.id}`}
                >
                  <PhotoThumbnail
                    photoId={photo.id}
                    alt=""
                    onClick={() => setLightboxIndex(idx)}
                    aria-label={`View photo ${idx + 1}`}
                  />
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Top-right climate badge */}
        <div className="shrink-0 flex flex-col items-end gap-1">
          {hasClimate && sit.conditions && (
            <div className="flex items-center gap-1.5 text-body-xs text-[var(--color-text-muted)]">
              {sit.conditions.tempF !== undefined && (
                <span className="flex items-center gap-0.5">
                  <Thermometer size={11} aria-hidden />
                  {Math.round(sit.conditions.tempF)}°
                </span>
              )}
              {sit.conditions.windSpeedMph !== undefined && (
                <span className="flex items-center gap-0.5">
                  <Wind size={11} aria-hidden />
                  {sit.conditions.windDirection ? `${sit.conditions.windDirection} ` : ''}
                  {Math.round(sit.conditions.windSpeedMph)}
                </span>
              )}
            </div>
          )}

          {/* Actions */}
          <Link
            to={`/hunt/${huntId}/harvest/new?sitId=${sit.id}`}
            className="text-body-xs text-[var(--color-accent)] hover:text-[var(--color-accent-hover)] whitespace-nowrap"
            data-testid={`log-harvest-from-sit-${sit.id}`}
          >
            Log Harvest
          </Link>
          {confirmDelete ? (
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => onDelete(sit.id)}
                className="text-body-xs text-red-400 hover:text-red-300 font-medium"
                data-testid={`confirm-delete-sit-${sit.id}`}
              >
                Delete
              </button>
              <button
                type="button"
                onClick={() => setConfirmDelete(false)}
                className="text-body-xs text-[var(--color-text-muted)] hover:text-[var(--color-text-secondary)]"
              >
                Cancel
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-1">
              {onEdit && (
                <button
                  type="button"
                  onClick={() => onEdit(sit.id)}
                  className="text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] transition-colors p-1"
                  aria-label="Edit sit"
                  data-testid={`edit-sit-btn-${sit.id}`}
                >
                  <Pencil size={14} />
                </button>
              )}
              <button
                type="button"
                onClick={() => setConfirmDelete(true)}
                className="text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] transition-colors p-1"
                aria-label="Delete sit"
                data-testid={`delete-sit-btn-${sit.id}`}
              >
                <X size={16} />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Lightbox -- uses usePhotoSrc internally (M81) */}
      {lightboxIndex !== null && thumbnailPhotos.length > 0 && (
        <PhotoLightbox
          photos={thumbnailPhotos}
          initialIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          sitContext={`${locationName} · ${periodLabel}`}
        />
      )}
    </div>
  );
}
